import React, { useEffect, useRef, useState } from "react";

const DRAG_THRESHOLD_PX = 6;
const VIEWPORT_EDGE_GAP_PX = 12;

export default function EchoBottomSheet({
  open,
  title,
  closeLabel = "Close",
  onClose,
  children
}) {
  const sheetRef = useRef(null);
  const dragStateRef = useRef(null);
  const [offsetY, setOffsetY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (!open) {
      setOffsetY(0);
      setIsDragging(false);
      dragStateRef.current = null;
    }
  }, [open]);

  useEffect(() => {
    const currentSheet = sheetRef.current;

    return () => {
      if (dragStateRef.current?.pointerId != null) {
        try {
          currentSheet?.releasePointerCapture?.(dragStateRef.current.pointerId);
        } catch (error) {
          // Ignore pointer-capture cleanup failures during unmount.
        }
      }
    };
  }, []);

  if (!open) {
    return null;
  }

  function handleDragStart(event) {
    if (event.pointerType === "mouse" && event.button !== 0) {
      return;
    }

    const rect = sheetRef.current?.getBoundingClientRect?.();

    if (!rect) {
      return;
    }

    dragStateRef.current = {
      pointerId: event.pointerId,
      startY: event.clientY,
      startTop: rect.top,
      startOffset: offsetY,
      height: rect.height,
      didDrag: false
    };

    try {
      sheetRef.current?.setPointerCapture?.(event.pointerId);
    } catch (error) {
      // Ignore unsupported pointer capture.
    }
  }

  function handleDragMove(event) {
    const dragState = dragStateRef.current;

    if (!dragState || dragState.pointerId !== event.pointerId) {
      return;
    }

    const deltaY = event.clientY - dragState.startY;

    if (!dragState.didDrag && Math.abs(deltaY) < DRAG_THRESHOLD_PX) {
      return;
    }

    if (!dragState.didDrag) {
      dragState.didDrag = true;
      setIsDragging(true);
    }

    const viewportHeight = window.innerHeight || 0;
    const minTop = VIEWPORT_EDGE_GAP_PX;
    const maxTop = Math.max(
      VIEWPORT_EDGE_GAP_PX,
      viewportHeight - dragState.height - VIEWPORT_EDGE_GAP_PX
    );
    const desiredTop = dragState.startTop + deltaY;
    const clampedTop = Math.max(minTop, Math.min(maxTop, desiredTop));

    setOffsetY(dragState.startOffset + (clampedTop - dragState.startTop));
  }

  function handleDragEnd(event) {
    const dragState = dragStateRef.current;

    if (!dragState || dragState.pointerId !== event.pointerId) {
      return;
    }

    try {
      sheetRef.current?.releasePointerCapture?.(event.pointerId);
    } catch (error) {
      // Ignore unsupported pointer capture release.
    }

    dragStateRef.current = null;
    setIsDragging(false);
  }

  return (
    <div
      className="echo-bottom-sheet-backdrop"
      onClick={onClose}
      role="presentation"
    >
      <div
        ref={sheetRef}
        className="echo-bottom-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
        onPointerMove={handleDragMove}
        onPointerUp={handleDragEnd}
        onPointerCancel={handleDragEnd}
        style={{
          transform: `translateY(${offsetY}px)`
        }}
      >
        <div
          className={`echo-bottom-sheet__handle ${isDragging ? "is-dragging" : ""}`}
          aria-hidden="true"
          onPointerDown={handleDragStart}
        />

        <div
          className={`echo-bottom-sheet__header ${isDragging ? "is-dragging" : ""}`}
          onPointerDown={handleDragStart}
        >
          <div className="echo-bottom-sheet__title">{title}</div>
          <button
            className="echo-bottom-sheet__close"
            type="button"
            onPointerDown={(event) => event.stopPropagation()}
            onClick={onClose}
          >
            {closeLabel}
          </button>
        </div>

        <div className="echo-bottom-sheet__body">{children}</div>
      </div>
    </div>
  );
}
